import { useMutation, Reference } from '@apollo/client';
import { useState, useCallback } from 'react';
import { completeInspectionInstanceMutation } from 'graphql-queries';

export interface CompleteInspectionInstanceInput {
  channelId: string;
  instanceId: string;
  completedAt: Date;
  notes?: string;
}

type CompleteInspectionInstanceMutationResult = {
  completeInspectionInstance: {
    success: boolean;
    newVersion: number;
  } | null;
};

type CompleteInspectionInstanceMutationVariables = {
  instanceId: string;
  channelId: string;
  completedAt: string;
  notes?: string;
};
export interface UseCompleteInspectionInstanceResult {
  completeInstance: (
    input: CompleteInspectionInstanceInput
  ) => Promise<{ success: boolean; newVersion: number }>;
  loading: boolean;
  error: Error | undefined;
}

export function useCompleteInspectionInstance(): UseCompleteInspectionInstanceResult {
  const [mutate] = useMutation<
    CompleteInspectionInstanceMutationResult,
    CompleteInspectionInstanceMutationVariables
  >(completeInspectionInstanceMutation);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  const completeInstance = useCallback(
    async (input: CompleteInspectionInstanceInput) => {
      setLoading(true);
      setError(undefined);

      try {
        const result = await mutate({
          variables: {
            instanceId: input.instanceId,
            channelId: input.channelId,
            completedAt: input.completedAt.toISOString(),
            notes: input.notes,
          },
          update(cache, { data }) {
            if (!data?.completeInspectionInstance?.success) return;

            // Remove the completed instance from all cached lists immediately.
            // cache.modify is used over cache.evict because InspectionInstanceResult
            // is stored inline (no id field) — eviction leaves a dangling null in
            // the array instead of removing the item cleanly.
            cache.modify({
              fields: {
                inspectionInstances(existingConnection, { readField }) {
                  if (!existingConnection) return existingConnection;

                  const existingInstances: readonly Reference[] =
                    existingConnection.inspectionInstances ?? [];

                  const filtered = existingInstances.filter(
                    (ref: Reference) =>
                      readField('id', ref) !== input.instanceId
                  );

                  if (filtered.length === existingInstances.length) {
                    return existingConnection;
                  }

                  return { ...existingConnection, inspectionInstances: filtered };
                },
              },
            });
          },
        });

        if (!result.data?.completeInspectionInstance?.success) {
          throw new Error('Failed to complete inspection instance');
        }

        return {
          success: result.data.completeInspectionInstance.success,
          newVersion: result.data.completeInspectionInstance.newVersion,
        };
      } catch (err) {
        const wrappedError =
          err instanceof Error ? err : new Error('Unknown error occurred');
        setError(wrappedError);
        throw wrappedError;
      } finally {
        setLoading(false);
      }
    },
    [mutate]
  );

  return {
    completeInstance,
    loading,
    error,
  };
}
